import { useEffect, useState, type FormEvent } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { LoaderCircle, ShieldCheck, Trash2, Unplug } from "lucide-react"
import { toast } from "sonner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { api, type GenesysIntegrationInput, type GenesysIntegrationStatus } from "@/lib/api"
import { queryKeys } from "@/lib/query-keys"

function statusLabel(status: GenesysIntegrationStatus["status"]) {
  switch (status) {
    case "active": return "Ativa"
    case "degraded": return "Com instabilidade"
    case "error": return "Com erro"
    default: return "Desativada"
  }
}

function statusClass(status: GenesysIntegrationStatus["status"]) {
  if (status === "active") return "border-success/30 bg-success/10 text-success"
  if (status === "degraded" || status === "error") return "border-destructive/30 bg-destructive/10 text-destructive"
  return "border-border bg-muted/40 text-muted-foreground"
}

/** Sem o código do erro, um encaminhamento recusado pela Genesys fica invisível. */
function handoffErrorCopy(code: string) {
  if (code.startsWith("crypto_")) return "As credenciais guardadas não puderam ser decifradas com a chave atual do servidor."
  if (code.startsWith("database_")) return "Erro no banco de dados ao registrar o encaminhamento. A tentativa pode ser repetida."
  if (code.startsWith("transition_")) return "Transição de estado inválida no encaminhamento; é um defeito do BubbleDesk."
  if (code.startsWith("unexpected_")) return "Falha inesperada no envio; o código acima aponta o ponto exato para o time técnico."
  switch (code) {
    case "provider_error": return "Falha inesperada no envio, registrada antes do diagnóstico detalhado."
    case "provider_rejected_request": return "A Genesys recusou a requisição. Confira as permissões do OAuth Client e o Integration ID."
    case "provider_unavailable": return "A Genesys respondeu com erro interno. Vale tentar novamente."
    case "provider_rate_limited": return "A Genesys limitou as requisições (429)."
    case "provider_transport_failed": return "Não foi possível conectar à API da Genesys (rede ou TLS)."
    case "authentication_failed": return "O OAuth Client não conseguiu obter o token."
    case "invalid_provider_response": return "A resposta da Genesys veio em formato inesperado."
    case "invalid_provider_configuration": return "A região ou o Integration ID estão ausentes ou inválidos."
    case "conversation_id_pending": return "A Genesys aceitou a mensagem mas não devolveu o ID da conversa."
    case "message_unavailable": return "O texto do encaminhamento não estava disponível."
    case "encryption_unavailable": return "A chave de criptografia do servidor está indisponível."
    case "chatbot_paused": return "O assistente foi pausado antes do envio."
    case "integration_disabled": return "A integração foi desativada antes do envio."
    default: return "Código não reconhecido; confira os registros da API."
  }
}

export function GenesysIntegrationSection({ chatbotId }: { chatbotId: string }) {
  const queryClient = useQueryClient()
  const [region, setRegion] = useState("")
  const [routingIntegrationId, setRoutingIntegrationId] = useState("")
  const [routingQueueValue, setRoutingQueueValue] = useState("CHAT_SAC_MILPLAY")
  const [oauthClientId, setOauthClientId] = useState("")
  const [oauthClientSecret, setOauthClientSecret] = useState("")
  const [webhookSecret, setWebhookSecret] = useState("")
  const statusQuery = useQuery({
    queryKey: queryKeys.genesysIntegration(chatbotId),
    queryFn: () => api.getGenesysIntegration(chatbotId),
    enabled: Boolean(chatbotId),
  })
  const status = statusQuery.data
  const selectedRegion = region || status?.region || ""

  useEffect(() => {
    setRoutingIntegrationId(status?.routingIntegrationId ?? "")
    setRoutingQueueValue(status?.routingQueueValue ?? "CHAT_SAC_MILPLAY")
    setOauthClientId("")
    setOauthClientSecret("")
    setWebhookSecret("")
  }, [chatbotId, status?.routingIntegrationId, status?.routingQueueValue])

  useEffect(() => {
    setRegion(status?.region ?? "")
  }, [chatbotId, status?.region])

  const saveMutation = useMutation({
    mutationFn: (input: GenesysIntegrationInput) => api.saveGenesysIntegration(chatbotId, input),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.genesysIntegration(chatbotId), next)
      setRegion(next.region ?? "")
      setRoutingIntegrationId(next.routingIntegrationId ?? "")
      setRoutingQueueValue(next.routingQueueValue)
      setOauthClientId("")
      setOauthClientSecret("")
      setWebhookSecret("")
      toast.success("Configuração Genesys salva com segurança.")
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Não foi possível salvar a integração."),
  })
  const verifyMutation = useMutation({
    mutationFn: () => api.verifyGenesysIntegration(chatbotId),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.genesysIntegration(chatbotId), next)
      toast.success("Credenciais OAuth verificadas.")
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.genesysIntegration(chatbotId) })
      toast.error(error instanceof Error ? error.message : "Não foi possível verificar as credenciais." )
    },
  })
  const statusMutation = useMutation({
    mutationFn: (next: "active" | "disabled") => api.setGenesysIntegrationStatus(chatbotId, next),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.genesysIntegration(chatbotId), next)
      toast.success(next.status === "active" ? "Integração ativada." : "Integração desativada.")
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Não foi possível alterar o status."),
  })
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteGenesysIntegration(chatbotId),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.genesysIntegration(chatbotId), undefined)
      void queryClient.invalidateQueries({ queryKey: queryKeys.genesysIntegration(chatbotId) })
      setRoutingIntegrationId("")
      setOauthClientId("")
      setOauthClientSecret("")
      setWebhookSecret("")
      toast.success("Integração removida. O histórico local foi preservado.")
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Não foi possível remover a integração."),
  })

  function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!selectedRegion) return toast.error("Selecione a região da organização Genesys.")
    if (!routingIntegrationId.trim()) return toast.error("Informe o Integration ID.")
    if (!routingQueueValue.trim()) return toast.error("Informe o valor da fila para roteamento.")
    if ((!status?.oauthClientIdConfigured && !oauthClientId.trim())
      || (!status?.oauthClientSecretConfigured && !oauthClientSecret)
      || (!status?.webhookSecretConfigured && !webhookSecret)) {
      return toast.error("Informe o Client ID, Client Secret e segredo do webhook para configurar.")
    }
    const input: GenesysIntegrationInput = {
      region: selectedRegion,
      routingIntegrationId: routingIntegrationId.trim(),
      routingQueueValue: routingQueueValue.trim(),
    }
    if (oauthClientId.trim()) input.oauthClientId = oauthClientId.trim()
    if (oauthClientSecret) input.oauthClientSecret = oauthClientSecret
    if (webhookSecret) input.webhookSecret = webhookSecret
    saveMutation.mutate(input)
  }

  if (statusQuery.isPending) return <div className="space-y-4" aria-busy="true"><Skeleton className="h-24 w-full rounded-2xl" /><Skeleton className="h-72 w-full rounded-2xl" /></div>
  if (statusQuery.error || !status) return <Alert variant="destructive"><Unplug className="size-4" /><AlertDescription>{statusQuery.error instanceof Error ? statusQuery.error.message : "Não foi possível carregar o status da integração."}</AlertDescription></Alert>

  const busy = saveMutation.isPending || verifyMutation.isPending || statusMutation.isPending || deleteMutation.isPending
  const hasUnsavedChanges = selectedRegion !== (status.region ?? "")
    || routingIntegrationId !== (status.routingIntegrationId ?? "")
    || routingQueueValue !== status.routingQueueValue
    || Boolean(oauthClientId || oauthClientSecret || webhookSecret)
  return (
    <div className="space-y-5">
      <section className="panel-bg rounded-[22px] border border-border p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-[19px] font-bold">Atendimento humano com Genesys Cloud</h2>
            <p className="muted mt-1 max-w-2xl text-[12px] leading-relaxed">Configure o OAuth e o webhook de mensagens para encaminhar o atendimento humano pelo Genesys Cloud.</p>
          </div>
          <Badge className={`rounded-full border px-3 py-1 text-[11px] ${statusClass(status.status)}`}>{statusLabel(status.status)}</Badge>
        </div>
        {status.status === "disabled" && <p role="status" className="muted mt-4 rounded-xl border border-border bg-background/40 p-3 text-[12px]">Com a integração desativada, o assistente virtual continua disponível. Se houver atendimento humano em andamento, o visitante recebe um aviso de indisponibilidade. As credenciais permanecem salvas.</p>}
        {status.lastError && <Alert variant="destructive" className="mt-4"><AlertDescription>Última verificação: {status.lastError.code} · {status.lastError.at ? new Date(status.lastError.at).toLocaleString("pt-BR") : "horário indisponível"}</AlertDescription></Alert>}
        {status.lastHandoffError && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>
              Último encaminhamento falhou ({status.lastHandoffError.code}): {handoffErrorCopy(status.lastHandoffError.code)} · {new Date(status.lastHandoffError.at).toLocaleString("pt-BR")}
            </AlertDescription>
          </Alert>
        )}
      </section>

      <section aria-labelledby="genesys-setup-guide" className="panel-bg rounded-[22px] border border-border p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 id="genesys-setup-guide" className="text-[16px] font-semibold">Guia rápido de configuração</h3>
            <p className="muted mt-1 text-[13px] leading-relaxed">Cada assistente tem sua própria conexão. Use os dados da organização Genesys que vai atender este assistente.</p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-[13px]">
            <a className="text-primary underline-offset-4 hover:underline" href="https://help.genesys.cloud/articles/create-an-oauth-client/" target="_blank" rel="noreferrer">Ajuda: criar OAuth</a>
            <a className="text-primary underline-offset-4 hover:underline" href="https://help.genesys.cloud/articles/configure-an-open-messaging-integration/" target="_blank" rel="noreferrer">Ajuda: Open Messaging</a>
          </div>
        </div>
        <ol className="mt-6 grid gap-6 sm:grid-cols-2">
          <li className="flex gap-4">
            <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background/60 text-[13px] font-semibold">1</span>
            <div className="space-y-1">
              <p className="text-[15px] font-medium">Crie o cliente OAuth no Genesys</p>
              <p className="muted text-[14px] leading-relaxed">Em Admin → Integrações → OAuth, crie um cliente com o tipo <strong>Client Credentials</strong> e atribua <code>conversation:message:receive</code>, <code>conversation:communication:view</code> e <code>routing:queue:view</code>. Elas permitem enviar mensagens pelo Open Messaging, consultar a fila e obter a estimativa de espera. Depois, copie o Client ID e o Client Secret.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background/60 text-[13px] font-semibold">2</span>
            <div className="space-y-1">
              <p className="text-[15px] font-medium">Crie a integração Open Messaging</p>
              <p className="muted text-[14px] leading-relaxed">Em Admin → Message → Platforms → Platform Integrations, crie uma integração <strong>Open Messaging</strong>. Use a URL abaixo em <strong>Outbound Notification Webhook URL</strong>, escolha um segredo aleatório para <strong>Outbound Notification Webhook Signature Secret Token</strong>, salve e copie o Integration ID.</p>
              {status.webhookUrl
                ? <code className="block break-all rounded-lg bg-muted/50 p-2 text-[12px]">{status.webhookUrl}</code>
                : <p className="rounded-lg border border-border bg-background/40 p-2 text-[12px]">A URL pública ainda não está disponível. O servidor precisa estar configurado com o endereço público HTTPS da aplicação.</p>}
            </div>
          </li>
          <li className="flex gap-4">
            <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background/60 text-[13px] font-semibold">3</span>
            <div className="space-y-1">
              <p className="text-[15px] font-medium">Preencha e salve este assistente</p>
              <p className="muted text-[14px] leading-relaxed">Selecione a região correspondente ao portal Genesys (por exemplo, <code>login.mypurecloud.ie</code> é Irlanda), informe o Integration ID e as credenciais OAuth. No campo do segredo do webhook, informe o mesmo valor escolhido no passo anterior.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background/60 text-[13px] font-semibold">4</span>
            <div className="space-y-1">
              <p className="text-[15px] font-medium">Verifique as credenciais OAuth</p>
              <p className="muted text-[14px] leading-relaxed">Volte aqui e clique em <strong>Verificar OAuth</strong>. Essa verificação confirma que o Client ID e o Client Secret conseguem obter um token.</p>
            </div>
          </li>
          <li className="flex gap-4 sm:col-span-2">
            <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background/60 text-[13px] font-semibold">5</span>
            <div className="space-y-1">
              <p className="text-[15px] font-medium">Ative a integração</p>
              <p className="muted text-[14px] leading-relaxed">Depois de salvar o Open Messaging com a URL definitiva e o mesmo segredo, clique em <strong>Ativar integração</strong>. A verificação OAuth não testa o webhook nem o roteamento da fila.</p>
            </div>
          </li>
          <li className="flex gap-4 sm:col-span-2">
            <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background/60 text-[13px] font-semibold">6</span>
            <div className="space-y-1">
              <p className="text-[15px] font-medium">Configure a fila e as atualizações de espera no Architect</p>
              <p className="muted text-[14px] leading-relaxed">O BubbleDesk envia o atributo de participante <code>fila</code> junto com o encaminhamento. No Architect, leia esse dado e encaminhe para a fila correspondente (o valor inicial é <code>CHAT_SAC_MILPLAY</code>). O BubbleDesk consulta a conversa para mostrar o nome da fila e a espera estimada; para exibir posição e outras atualizações periódicas, configure o fluxo na fila para enviá-las pelo webhook do Open Messaging.</p>
            </div>
          </li>
        </ol>
      </section>

      <form onSubmit={(event) => void save(event)} className="panel-bg space-y-5 rounded-[22px] border border-border p-5 sm:p-6">
        <div>
          <h3 className="text-[14px] font-semibold">Credenciais e integração</h3>
          <p className="muted mt-1 text-[11px]">Os segredos são enviados apenas neste formulário e ficam cifrados no servidor. Campos secretos aparecem vazios após salvar.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="genesys-region">Região da organização Genesys</Label>
            <Select value={selectedRegion || undefined} onValueChange={setRegion}>
              <SelectTrigger id="genesys-region" disabled={!status.canManage || busy}>
                <SelectValue placeholder="Selecione a região exibida no portal Genesys" />
              </SelectTrigger>
              <SelectContent>
                {status.regions.map((item) => <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="muted text-[11px]">A região fica salva apenas neste assistente. Confira o domínio do portal Genesys da organização conectada.</p>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="genesys-integration-id">Integration ID</Label>
            <Input id="genesys-integration-id" value={routingIntegrationId} onChange={(event) => setRoutingIntegrationId(event.target.value)} maxLength={200} autoComplete="off" disabled={!status.canManage || busy} required />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="genesys-routing-queue">Valor do atributo de participante <code>fila</code></Label>
            <Input id="genesys-routing-queue" value={routingQueueValue} onChange={(event) => setRoutingQueueValue(event.target.value)} maxLength={200} autoComplete="off" disabled={!status.canManage || busy} required />
            <p className="muted text-[11px]">Enviado como <code>channel.metadata.customAttributes.fila</code> no início do encaminhamento. Valor inicial: <code>CHAT_SAC_MILPLAY</code>.</p>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="genesys-oauth-client-id">OAuth Client ID {status.oauthClientIdConfigured && <span className="muted font-normal">(vazio mantém o salvo)</span>}</Label>
            <Input id="genesys-oauth-client-id" value={oauthClientId} onChange={(event) => setOauthClientId(event.target.value)} placeholder={status.oauthClientIdConfigured ? "••••••••••••••••" : undefined} maxLength={500} autoComplete="off" disabled={!status.canManage || busy} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="genesys-oauth-client-secret">OAuth Client Secret {status.oauthClientSecretConfigured && <span className="muted font-normal">(vazio mantém o salvo)</span>}</Label>
            <Input id="genesys-oauth-client-secret" type="password" value={oauthClientSecret} onChange={(event) => setOauthClientSecret(event.target.value)} placeholder={status.oauthClientSecretConfigured ? "••••••••••••••••" : undefined} maxLength={2_000} autoComplete="new-password" spellCheck={false} disabled={!status.canManage || busy} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="genesys-webhook-secret">Segredo de assinatura do webhook {status.webhookSecretConfigured && <span className="muted font-normal">(vazio mantém o salvo)</span>}</Label>
            <Input id="genesys-webhook-secret" type="password" value={webhookSecret} onChange={(event) => setWebhookSecret(event.target.value)} placeholder={status.webhookSecretConfigured ? "••••••••••••••••" : undefined} maxLength={2_000} autoComplete="new-password" spellCheck={false} disabled={!status.canManage || busy} />
          </div>
        </div>
        {(status.oauthClientConfigured || status.webhookSecretConfigured) && <div className="grid gap-3 rounded-xl border border-border bg-background/40 p-3 text-[11px] sm:grid-cols-2">{status.oauthClientConfigured && <p className="flex items-center gap-2"><ShieldCheck className="size-4 text-success" />OAuth Client configurado</p>}{status.webhookSecretConfigured && <p className="flex items-center gap-2"><ShieldCheck className="size-4 text-success" />Segredo do webhook configurado</p>}</div>}
        {status.configured && !status.region && <p role="status" className="muted rounded-xl border border-border p-3 text-[11px]">Selecione e salve a região desta organização para verificar o OAuth.</p>}
        {status.configured && !status.lastVerifiedAt && <p role="status" className="muted rounded-xl border border-border p-3 text-[11px]">Verifique o OAuth depois de salvar. A ativação só fica disponível após essa verificação.</p>}
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
          {status.canManage && <Button type="submit" disabled={busy || !selectedRegion || !routingIntegrationId.trim()}>{saveMutation.isPending ? <LoaderCircle className="mr-2 size-4 animate-spin" /> : null}Salvar configuração</Button>}
          {status.canManage && status.configured && <Button type="button" variant="outline" disabled={busy || !status.region || hasUnsavedChanges} onClick={() => verifyMutation.mutate()}>{verifyMutation.isPending ? <LoaderCircle className="mr-2 size-4 animate-spin" /> : null}Verificar OAuth</Button>}
          {status.canManage && status.configured && status.status !== "active" && <Button type="button" variant="outline" disabled={busy || hasUnsavedChanges || !status.region || !status.lastVerifiedAt || !status.webhookUrl} onClick={() => statusMutation.mutate("active")}>
            {statusMutation.isPending ? <LoaderCircle className="mr-2 size-4 animate-spin" /> : null}
            {status.lastVerifiedAt ? "Ativar integração" : "Verificar OAuth para ativar"}
          </Button>}
          {status.canManage && status.status === "active" && <AlertDialog><AlertDialogTrigger asChild><Button type="button" variant="outline" disabled={busy}>Desativar</Button></AlertDialogTrigger><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Desativar integração Genesys?</AlertDialogTitle><AlertDialogDescription>Atendimentos humanos em andamento serão encerrados no BubbleDesk e o visitante receberá um aviso de indisponibilidade. As credenciais continuarão salvas.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => statusMutation.mutate("disabled")}>Desativar integração</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>}
          {status.canManage && status.configured && <AlertDialog><AlertDialogTrigger asChild><Button type="button" variant="destructive" disabled={busy} className="sm:ml-auto"><Trash2 className="mr-2 size-4" />Remover integração</Button></AlertDialogTrigger><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Remover conexão Genesys?</AlertDialogTitle><AlertDialogDescription>As credenciais, vínculos e itens pendentes da integração serão removidos. As conversas e mensagens locais continuam no histórico.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => deleteMutation.mutate()}>Remover</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>}
        </div>
        {!status.canManage && <p role="status" className="muted border-t border-border pt-4 text-[11px]">Você tem acesso de leitura. Somente o dono deste assistente ou um superadmin pode alterar a integração.</p>}
      </form>
    </div>
  )
}
